import type { CommandEntryName } from "./types";

type CommandEntryCopy = {
  signInAction: string | null;
  profileAction: string | null;
  cliUpdateAction: string;
  serviceAction: string | null;
};

const DEFAULT_COPY: CommandEntryCopy = {
  signInAction: "continuing with this command",
  profileAction: "continuing with this command",
  cliUpdateAction: "using WakeLoop CLI",
  serviceAction: null,
};

const COMMAND_ENTRY_COPY_BY_NAME: Readonly<
  Record<CommandEntryName, CommandEntryCopy>
> = Object.freeze({
  root: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "opening WakeLoop",
    serviceAction: null,
  },
  view: { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  start: {
    signInAction: "continuing setup",
    profileAction: "continuing setup",
    cliUpdateAction: "starting WakeLoop",
    serviceAction: "starting WakeLoop",
  },
  setup: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "setting up WakeLoop on this computer",
    serviceAction: null,
  },
  feedback: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "sending feedback to WakeLoop",
    serviceAction: null,
  },
  "repair.local-state": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "repairing WakeLoop",
  },
  uninstall: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "uninstalling WakeLoop",
  },
  reset: { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  upgrade: {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "upgrading WakeLoop CLI",
  },
  "auth.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.status": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.login": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "auth.logout": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "organization.list": {
    signInAction: "listing organizations",
    profileAction: null,
    cliUpdateAction: "listing organizations",
    serviceAction: null,
  },
  "organization.current": {
    signInAction: "showing the active organization",
    profileAction: null,
    cliUpdateAction: "showing the active organization",
    serviceAction: null,
  },
  "organization.switch": {
    signInAction: "switching organizations",
    profileAction: null,
    cliUpdateAction: "switching organizations",
    serviceAction: null,
  },
  "binding.menu": {
    signInAction: "managing Binding authorizations",
    profileAction: null,
    cliUpdateAction: "managing Binding authorizations",
    serviceAction: null,
  },
  "binding.authorize": {
    signInAction: "authorizing this Binding",
    profileAction: null,
    cliUpdateAction: "authorizing this Binding",
    serviceAction: null,
  },
  "binding.revoke": {
    signInAction: "revoking this Binding authorization",
    profileAction: null,
    cliUpdateAction: "revoking this Binding authorization",
    serviceAction: null,
  },
  "binding.list": {
    signInAction: "listing Binding authorizations",
    profileAction: null,
    cliUpdateAction: "listing Binding authorizations",
    serviceAction: null,
  },
  "center_agent.menu": {
    signInAction: "managing the Center Agent",
    profileAction: null,
    cliUpdateAction: "managing the Center Agent",
    serviceAction: null,
  },
  "center_agent.status": {
    signInAction: "checking the Center Agent",
    profileAction: null,
    cliUpdateAction: "checking the Center Agent",
    serviceAction: null,
  },
  "center_agent.designate": {
    signInAction: "designating the Center Agent",
    profileAction: null,
    cliUpdateAction: "designating the Center Agent",
    serviceAction: null,
  },
  "center_agent.revoke": {
    signInAction: "revoking the Center Agent designation",
    profileAction: null,
    cliUpdateAction: "revoking the Center Agent designation",
    serviceAction: null,
  },
  "center_agent.audit": {
    signInAction: "listing Center Agent audit entries",
    profileAction: null,
    cliUpdateAction: "listing Center Agent audit entries",
    serviceAction: null,
  },
  "routines.menu": {
    signInAction: "managing Routines",
    profileAction: null,
    cliUpdateAction: "managing Routines",
    serviceAction: null,
  },
  "routines.list": {
    signInAction: "listing Routines",
    profileAction: null,
    cliUpdateAction: "listing Routines",
    serviceAction: null,
  },
  "routines.create": {
    signInAction: "creating a Routine",
    profileAction: null,
    cliUpdateAction: "creating a Routine",
    serviceAction: null,
  },
  "routines.show": {
    signInAction: "showing a Routine",
    profileAction: null,
    cliUpdateAction: "showing a Routine",
    serviceAction: null,
  },
  "routines.update": {
    signInAction: "updating a Routine",
    profileAction: null,
    cliUpdateAction: "updating a Routine",
    serviceAction: null,
  },
  "routines.pause": {
    signInAction: "pausing a Routine",
    profileAction: null,
    cliUpdateAction: "pausing a Routine",
    serviceAction: null,
  },
  "routines.resume": {
    signInAction: "resuming a Routine",
    profileAction: null,
    cliUpdateAction: "resuming a Routine",
    serviceAction: null,
  },
  "routines.delete": {
    signInAction: "deleting a Routine",
    profileAction: null,
    cliUpdateAction: "deleting a Routine",
    serviceAction: null,
  },
  "routines.runs": {
    signInAction: "listing Routine Runs",
    profileAction: null,
    cliUpdateAction: "listing Routine Runs",
    serviceAction: null,
  },
  "projects.menu": {
    signInAction: "managing Agent Activity Projects",
    profileAction: null,
    cliUpdateAction: "managing Agent Activity Projects",
    serviceAction: null,
  },
  "projects.list": {
    signInAction: "listing Agent Activity Projects",
    profileAction: null,
    cliUpdateAction: "listing Agent Activity Projects",
    serviceAction: null,
  },
  "projects.create": {
    signInAction: "creating an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "creating an Agent Activity Project",
    serviceAction: null,
  },
  "projects.update": {
    signInAction: "updating an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "updating an Agent Activity Project",
    serviceAction: null,
  },
  "projects.archive": {
    signInAction: "archiving an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "archiving an Agent Activity Project",
    serviceAction: null,
  },
  "projects.bind": {
    signInAction: "binding an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "binding an Agent Activity Project",
    serviceAction: null,
  },
  "projects.connections.menu": {
    signInAction: "managing local Project connections",
    profileAction: null,
    cliUpdateAction: "managing local Project connections",
    serviceAction: null,
  },
  "projects.connections.list": {
    signInAction: "listing local Project connections",
    profileAction: null,
    cliUpdateAction: "listing local Project connections",
    serviceAction: null,
  },
  "projects.connections.remove": {
    signInAction: "removing a local Project connection",
    profileAction: null,
    cliUpdateAction: "removing a local Project connection",
    serviceAction: null,
  },
  "projects.connections.set-default": {
    signInAction: "changing the default Project connection",
    profileAction: null,
    cliUpdateAction: "changing the default Project connection",
    serviceAction: null,
  },
  "projects.unbind": {
    signInAction: "unbinding an Agent Activity Project",
    profileAction: null,
    cliUpdateAction: "unbinding an Agent Activity Project",
    serviceAction: null,
  },
  "skills.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "skills.ensure": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.install": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.uninstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "skills.list": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "skills.check": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "skills.update": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.detect": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.list": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.manage": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.enable": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.repair": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.disable": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.show": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "agents.custom.list": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.add": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.update": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.custom.remove": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "agents.config": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "computer.list": {
    signInAction: "listing computers",
    profileAction: null,
    cliUpdateAction: "listing computers",
    serviceAction: null,
  },
  "computer.current": {
    signInAction: "showing this computer",
    profileAction: null,
    cliUpdateAction: "showing this computer",
    serviceAction: null,
  },
  "computer.rename": {
    signInAction: "renaming this computer",
    profileAction: null,
    cliUpdateAction: "renaming this computer",
    serviceAction: "renaming this computer",
  },
  "service.menu": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "service.run": {
    signInAction: "starting WakeLoop Service",
    profileAction: null,
    cliUpdateAction: "starting WakeLoop Service",
    serviceAction: "starting WakeLoop Service",
  },
  "service.status": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.interrupt": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.cancel": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.snapshot": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.trace": {
    signInAction: "reading a dispatch trace",
    profileAction: "reading a dispatch trace",
    cliUpdateAction: "reading a dispatch trace",
    serviceAction: null,
  },
  "service.install": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.autostart": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "changing WakeLoop Service automatic startup",
  },
  "service.uninstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.reinstall": {
    ...DEFAULT_COPY,
    signInAction: null,
    profileAction: null,
  },
  "service.stop": { ...DEFAULT_COPY, signInAction: null, profileAction: null },
  "space.menu": {
    signInAction: "managing spaces",
    profileAction: "managing spaces",
    cliUpdateAction: "opening Spaces",
    serviceAction: null,
  },
  "space.create": {
    signInAction: "creating a space",
    profileAction: "creating a space",
    cliUpdateAction: "creating a space",
    serviceAction: null,
  },
  "space.delete": {
    signInAction: "deleting a space",
    profileAction: "deleting a space",
    cliUpdateAction: "deleting a space",
    serviceAction: null,
  },
  "space.join": {
    signInAction: "joining a space",
    profileAction: "joining a space",
    cliUpdateAction: "joining a space",
    serviceAction: null,
  },
  "space.add-me": {
    signInAction: "adding yourself to a space",
    profileAction: "adding yourself to a space",
    cliUpdateAction: "adding yourself to a space",
    serviceAction: null,
  },
  "space.add-agents": {
    signInAction: "adding agent profiles to a space",
    profileAction: "adding agent profiles to a space",
    cliUpdateAction: "adding agent profiles to a space",
    serviceAction: null,
  },
  "space.add-human-profiles": {
    signInAction: "adding human profiles to a space",
    profileAction: "adding human profiles to a space",
    cliUpdateAction: "adding human profiles to a space",
    serviceAction: null,
  },
  "space.add-members": {
    signInAction: "adding members to a space",
    profileAction: "adding members to a space",
    cliUpdateAction: "adding members to a space",
    serviceAction: null,
  },
  "space.remove-members": {
    signInAction: "removing members from a space",
    profileAction: "removing members from a space",
    cliUpdateAction: "removing members from a space",
    serviceAction: null,
  },
  "space.leave": {
    signInAction: "leaving a space",
    profileAction: "leaving a space",
    cliUpdateAction: "leaving a space",
    serviceAction: null,
  },
  "space.watch": {
    signInAction: "watching a space",
    profileAction: "watching a space",
    cliUpdateAction: "watching a space",
    serviceAction: null,
  },
  "space.send": {
    signInAction: "sending a message",
    profileAction: "sending a message",
    cliUpdateAction: "sending a message",
    serviceAction: null,
  },
  "space.history": {
    signInAction: "reading space history",
    profileAction: "reading space history",
    cliUpdateAction: "reading space history",
    serviceAction: null,
  },
  "space.thread": {
    signInAction: "reading a space thread",
    profileAction: "reading a space thread",
    cliUpdateAction: "reading a space thread",
    serviceAction: null,
  },
  "space.board": {
    signInAction: "reading a space board",
    profileAction: "reading a space board",
    cliUpdateAction: "reading a space board",
    serviceAction: null,
  },
  "space.contract": {
    signInAction: "reading a space contract",
    profileAction: "reading a space contract",
    cliUpdateAction: "reading a space contract",
    serviceAction: null,
  },
  "space.contract.set": {
    signInAction: "updating a space contract",
    profileAction: "updating a space contract",
    cliUpdateAction: "updating a space contract",
    serviceAction: null,
  },
  "space.publication.set": {
    signInAction: "updating Space publication mode",
    profileAction: "updating Space publication mode",
    cliUpdateAction: "updating Space publication mode",
    serviceAction: null,
  },
  "space.status": {
    signInAction: "reading space status",
    profileAction: "reading space status",
    cliUpdateAction: "reading space status",
    serviceAction: null,
  },
  "space.updates": {
    signInAction: "reading space updates",
    profileAction: "reading space updates",
    cliUpdateAction: "reading space updates",
    serviceAction: null,
  },
  "space.result": {
    signInAction: "reading a space dispatch result",
    profileAction: "reading a space dispatch result",
    cliUpdateAction: "reading a space dispatch result",
    serviceAction: null,
  },
  "space.list": {
    signInAction: "listing spaces",
    profileAction: "listing spaces",
    cliUpdateAction: "listing spaces",
    serviceAction: null,
  },
  "space.password": {
    signInAction: "updating a space password",
    profileAction: "updating a space password",
    cliUpdateAction: "updating a space password",
    serviceAction: null,
  },
  "space.guide": {
    signInAction: "reading space guide",
    profileAction: "reading space guide",
    cliUpdateAction: "reading space guide",
    serviceAction: null,
  },
  "space.guide.set": {
    signInAction: "updating space guide",
    profileAction: "updating space guide",
    cliUpdateAction: "updating space guide",
    serviceAction: null,
  },
  "space.guide.clear": {
    signInAction: "clearing space guide",
    profileAction: "clearing space guide",
    cliUpdateAction: "clearing space guide",
    serviceAction: null,
  },
  doctor: {
    signInAction: "running doctor checks",
    profileAction: "running doctor checks",
    cliUpdateAction: "running doctor checks",
    serviceAction: null,
  },
  whoami: {
    signInAction: null,
    profileAction: null,
    cliUpdateAction: "checking current WakeLoop status",
    serviceAction: null,
  },
  "profile.menu": {
    signInAction: "managing profiles",
    profileAction: null,
    cliUpdateAction: "managing profiles",
    serviceAction: null,
  },
  "profile.create": {
    signInAction: "creating a profile",
    profileAction: null,
    cliUpdateAction: "creating a profile",
    serviceAction: null,
  },
  "profile.list": {
    signInAction: "listing profiles",
    profileAction: null,
    cliUpdateAction: "listing profiles",
    serviceAction: null,
  },
  "profile.set": {
    signInAction: "choosing a profile",
    profileAction: null,
    cliUpdateAction: "choosing a profile",
    serviceAction: null,
  },
  "profile.show": {
    signInAction: "showing a profile",
    profileAction: null,
    cliUpdateAction: "showing a profile",
    serviceAction: null,
  },
  "profile.update": {
    signInAction: "updating a profile",
    profileAction: null,
    cliUpdateAction: "updating a profile",
    serviceAction: null,
  },
  "profile.delete": {
    signInAction: "deleting a profile",
    profileAction: null,
    cliUpdateAction: "deleting a profile",
    serviceAction: null,
  },
});

export function getCommandEntryCopy(
  entryName: CommandEntryName
): CommandEntryCopy {
  return COMMAND_ENTRY_COPY_BY_NAME[entryName];
}
